import { DataGrid, ptBR, enUS } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';
import CardList from './CardList';

export default function DataTable(props) {
    const {
        id,
        checkboxSelection = false,
        disableSelectionOnClick = false,
        rowHeight = 52,
        columns = null,
        mobileColumns = null,
        desktopColumns = null,
        rows,
        pageSize = 50,
        rowsPerPageOptions = [50],
        disableMultipleSelection = true,
        handleSelectChange,
        // Optional: hand it in and the grid shows what the caller holds rather than
        // what it happens to remember. Products needs this because its list is
        // unmounted while the product form owns the screen.
        selectionModel,
        // Hand one in and the table becomes a card list below the breakpoint.
        // Opt-in on purpose: a screen with no card designed for it is better off
        // with a cramped grid than with a generic one built from its columns.
        renderCard = null,
        // Given the row, not the grid's params object: a caller must not have to
        // know whether it is talking to a grid or a card list.
        onRowClick = null,
        getRowClassName,
        sx,
        hideFooter = false,
        sortModel,
        loading = false,
        // Server-side pagination. Defaults keep every existing caller paging its own
        // fully-loaded `rows` client-side, exactly as before.
        paginationMode = 'client',
        page,
        onPageChange,
        onPageSizeChange,
        rowCount
    } = props;

    // Asked here rather than passed in. Callers used to hand over
    // `windowWidth={window.innerWidth}`, read once at render with nothing
    // subscribed to it, so a rotation never reached the grid -- and two callers
    // simply left the prop commented out, which silently meant "desktop always".
    const isMobile = useIsMobile();
    // The fallbacks are load-bearing: ConfirmSales and PayCommissions pass
    // desktopColumns alone, and asking a phone-width grid for columns that were
    // never written would render nothing at all.
    const gridColumns = columns
        ? columns
        : isMobile
            ? (mobileColumns || desktopColumns)
            : (desktopColumns || mobileColumns);

    const { i18n } = useTranslation();

    // The grid's own footer and menus ship translated; without this they stay
    // English no matter what the toggle says. The card list borrows the same
    // catalog so the two modes do not drift apart in one language.
    const localeText = (i18n.language === 'pt' ? ptBR : enUS)
        .components.MuiDataGrid.defaultProps.localeText;

    if (isMobile && renderCard) {
        return (
            <CardList
                rows={rows}
                renderCard={renderCard}
                checkboxSelection={checkboxSelection}
                disableMultipleSelection={disableMultipleSelection}
                disableSelectionOnClick={disableSelectionOnClick}
                onRowClick={onRowClick}
                selectionModel={selectionModel}
                handleSelectChange={handleSelectChange}
                loading={loading}
                localeText={localeText}
                paginationMode={paginationMode}
                page={page}
                pageSize={pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                rowCount={rowCount}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
            />
        );
    }

    return (
        <DataGrid
            id={id}
            checkboxSelection={checkboxSelection}
            disableSelectionOnClick={disableSelectionOnClick}
            rowHeight={rowHeight}
            columns={gridColumns}
            rows={rows}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            disableMultipleSelection={disableMultipleSelection}
            onSelectionModelChange={handleSelectChange}
            selectionModel={selectionModel}
            onRowClick={onRowClick ? params => onRowClick(params.row) : undefined}
            getRowClassName={getRowClassName}
            sx={sx}
            hideFooter={hideFooter}
            sortModel={sortModel}
            loading={loading}
            localeText={localeText}
            paginationMode={paginationMode}
            page={page}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            rowCount={rowCount}
        />
    );
}
